/**
 * sessionGreeting — Argo is the front door: EVERY session opens with the
 * assistant speaking first. Never silent, ever.
 *
 * THE MANDATE (operator, verbatim spirit): "Viktor proactively makes
 * suggestions, does research, reminds you of things, asks where it can
 * help." The old behavior — a once-per-day standup or NOTHING — made
 * silence the common case. This module inverts that: composeSessionGreeting
 * is TOTAL. Given any inputs it returns a greeting; a null return is a type
 * error, not a runtime case.
 *
 * Priority ladder (first match wins):
 *   (a) 'standup'    — first session of the day AND real work is ready →
 *                      the full morning standup (the existing tested
 *                      composeStandup output rides along unchanged).
 *   (b) 'while-away' — agents ran since the operator last looked → an
 *                      honest "While you were away: …" digest built from
 *                      REAL feed entries, with the top nudges as chips.
 *   (c) 'nudges'     — nothing new ran, but the live sweep has suggestions
 *                      → greeting + the top 2–3 suggestions as chips.
 *   (d) 'cold-start' — genuinely nothing AND an empty workspace → the
 *                      existing cold-start ask (give me your business).
 *   (e) 'all-clear'  — genuinely nothing but a populated workspace →
 *                      honest but ALIVE: "All clear right now — want me to
 *                      draft this week's posts, research a prospect, or
 *                      sweep for risks?" with those three chips.
 *
 * Honesty contract unchanged: every line comes from real records the
 * caller gathered (standup counts, feed entries, nudges). This module only
 * formats and prioritizes — it never invents work. What changed is that
 * "nothing to report" now produces an honest, useful offer instead of
 * silence.
 */

import {
  composeColdStartStandup,
  standupGreetingWord,
  type Standup,
} from './standup';

// --- Inputs -----------------------------------------------------------------

/** The slice of an activity-feed entry the greeting digests (structural —
 *  the real AgentFeedEntry satisfies it). */
export interface FeedDigestEntry {
  agentName: string;
  /** Honest run summary, e.g. "scanned 12 records, found 3 issues, …". */
  summary: string;
  proposalsQueued: number;
  /** ISO-8601 timestamp of the run. */
  ts: string;
}

/** The slice of a proactive Nudge the greeting renders as a chip
 *  (structural — the real Nudge satisfies it). */
export interface NudgeChipInput {
  id: string;
  /** "Aisha Khan is at-risk" — the observation. */
  headline: string;
  /** Button label for the governed action, e.g. "Draft check-in". */
  actionLabel: string;
}

export interface SessionGreetingInputs {
  /** True when the once-per-day stamp says the standup hasn't shown today.
   *  It only DEMOTES variant (a) to a lighter greeting — never suppresses. */
  firstSessionOfDay: boolean;
  /** composeStandup over real counts — null when nothing is ready. */
  standup: Standup | null;
  /** Feed entries newer than lastSeenFeedAt (see feedEntriesSince). */
  feedSinceLastSeen: FeedDigestEntry[];
  /** The live proactive-nudge sweep, best first. */
  topNudges: NudgeChipInput[];
  userName?: string;
  /** True only for a genuinely cold store (no people, no accounts). */
  workspaceEmpty: boolean;
}

// --- Output -----------------------------------------------------------------

export type SessionGreetingVariant =
  | 'standup'
  | 'while-away'
  | 'nudges'
  | 'cold-start'
  | 'all-clear';

/** One clickable chip on the greeting card. */
export type GreetingChip =
  /** Accept a real proactive nudge — queues its governed action. */
  | { kind: 'nudge'; label: string; nudgeId: string }
  /** Navigate to the Content Engine to draft this week's posts. */
  | { kind: 'draft-posts'; label: string }
  /** Prefill the composer with a research ask (real Claude worker path). */
  | { kind: 'research-prospect'; label: string }
  /** Run the system scanner via the existing chat mechanics and report. */
  | { kind: 'sweep-risks'; label: string };

export interface SessionGreeting {
  variant: SessionGreetingVariant;
  /** Time-honest salutation: "Morning Dana." / "Evening." */
  greeting: string;
  /** The card's body line — always from real data, never filler. */
  body: string;
  chips: GreetingChip[];
  /**
   * Present for 'standup' and 'cold-start': the existing Standup object,
   * so hosts keep rendering the tested standup card (with its Review /
   * Approve-all / Not-now or Set-up chips) unchanged.
   */
  standup?: Standup;
}

// --- Helpers ----------------------------------------------------------------

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

function salute(now: number | Date, userName?: string): string {
  const first = userName?.trim().split(/\s+/)[0];
  const word = standupGreetingWord(now);
  return first ? `${word} ${first}.` : `${word}.`;
}

/**
 * PURE: the "while you were away" diff. Entries strictly newer than the
 * lastSeenFeedAt stamp, newest first. A null stamp (first-ever session)
 * yields [] — with no baseline, "while you were away" would be a lie.
 */
export function feedEntriesSince<T extends { ts: string }>(
  entries: T[],
  lastSeenAt: string | null | undefined,
): T[] {
  if (!lastSeenAt) return [];
  const seen = new Date(lastSeenAt).getTime();
  if (Number.isNaN(seen)) return [];
  return entries
    .filter((e) => {
      const t = new Date(e.ts).getTime();
      return !Number.isNaN(t) && t > seen;
    })
    .sort((a, b) => (a.ts < b.ts ? 1 : -1));
}

/** One honest digest line per run: lead with queued proposals when any. */
function feedLine(e: FeedDigestEntry): string {
  return e.proposalsQueued > 0
    ? `${e.agentName} queued ${plural(e.proposalsQueued, 'proposal')}`
    : `${e.agentName} ${e.summary}`;
}

/** How many nudges become chips on the lighter greetings. */
export const GREETING_NUDGE_LIMIT = 3;

/** How many feed runs the while-away digest names before "and N more". */
const FEED_DIGEST_LIMIT = 3;

/** The three always-available offers for a quiet, populated workspace. */
export const ALL_CLEAR_CHIPS: GreetingChip[] = [
  { kind: 'draft-posts', label: "Draft this week's posts" },
  { kind: 'research-prospect', label: 'Research a prospect' },
  { kind: 'sweep-risks', label: 'Sweep for risks' },
];

const nudgeChips = (nudges: NudgeChipInput[]): GreetingChip[] =>
  nudges.slice(0, GREETING_NUDGE_LIMIT).map((n) => ({
    kind: 'nudge' as const,
    label: n.actionLabel,
    nudgeId: n.id,
  }));

// --- The composer -----------------------------------------------------------

/**
 * TOTAL: always returns a greeting, by the priority ladder above.
 * Silence is a bug — there is no input for which this returns null.
 */
export function composeSessionGreeting(
  now: number | Date,
  inputs: SessionGreetingInputs,
): SessionGreeting {
  const greeting = salute(now, inputs.userName);

  // (a) First session of the day with real work ready → the full standup.
  if (inputs.firstSessionOfDay && inputs.standup && inputs.standup.counts.total > 0) {
    return {
      variant: 'standup',
      greeting: inputs.standup.greeting,
      body: `Ready for you: ${inputs.standup.lines.join(', ')}.`,
      chips: [],
      standup: inputs.standup,
    };
  }

  // (b) Agents ran since the operator last looked → honest digest.
  if (inputs.feedSinceLastSeen.length > 0) {
    const named = inputs.feedSinceLastSeen.slice(0, FEED_DIGEST_LIMIT);
    const more = inputs.feedSinceLastSeen.length - named.length;
    const digest = named.map(feedLine).join('; ');
    return {
      variant: 'while-away',
      greeting,
      body: `While you were away: ${digest}${
        more > 0 ? ` — and ${plural(more, 'more run')}` : ''
      }.`,
      chips: nudgeChips(inputs.topNudges),
    };
  }

  // (c) Nothing new ran, but the live sweep has suggestions.
  if (inputs.topNudges.length > 0) {
    const top = inputs.topNudges[0];
    return {
      variant: 'nudges',
      greeting,
      body: `${top.headline} — here's where I can help right now:`,
      chips: nudgeChips(inputs.topNudges),
    };
  }

  // (d) Genuinely nothing + an empty workspace → the cold-start ask.
  if (inputs.workspaceEmpty) {
    const cold = composeColdStartStandup(now, inputs.userName);
    return {
      variant: 'cold-start',
      greeting: cold.greeting,
      body:
        "I don't have anything to work on yet — your workspace is empty. " +
        'Give me your business and I\u2019ll start drafting, chasing, and briefing you every day.',
      chips: [],
      standup: cold,
    };
  }

  // (e) Genuinely nothing, populated workspace → honest but ALIVE.
  return {
    variant: 'all-clear',
    greeting,
    body:
      "All clear right now — want me to draft this week's posts, research " +
      'a prospect, or sweep for risks?',
    chips: ALL_CLEAR_CHIPS,
  };
}
